import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { CampaignService as CampaignsSDK, Statuses } from '@vendasta/campaigns';
import { ApiService } from '../api-service/api.service';
import {
  Campaign,
  LookupCampaignsResponse,
  RecipientCampaignsResponse,
  RecipientCampaignStatusResponse,
  SimpleCampaign,
} from './campaign';

const GET_CAMPAIGN_URL = '/_ajax/v1/campaign/get';

@Injectable({ providedIn: 'root' })
export class CampaignService {
  private campaignsSubject: BehaviorSubject<Campaign[]> = new BehaviorSubject<Campaign[]>(null);

  constructor(
    private apiService: ApiService,
    private readonly http: HttpClient,
    private readonly campaignsSDK: CampaignsSDK,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
  ) {}

  getCampaign(campaignId: string): Observable<Campaign> {
    return this.apiService.get(GET_CAMPAIGN_URL + '?campaignId=' + campaignId).pipe(
      map((result) => {
        return Campaign.createFromRawData(result);
      }),
    );
  }

  toggleUserOnCampaign(
    campaignId: string,
    recipientCampaignId: string,
    currentStatus: string,
    userId: string,
    accountGroupId: string,
  ): Observable<RecipientCampaignStatusResponse> {
    const url = '/_ajax/v1/recipient-campaign/status/';
    const body = {
      campaignId: campaignId,
      recipientCampaignId: recipientCampaignId,
      status: currentStatus === 'active' ? 'paused' : 'active',
      userId: userId,
      accountGroupId: accountGroupId,
    };

    return this.apiService.post(url, body).pipe(
      map((r) => ({
        recipientCampaignId: r['recipient_campaign_id'],
        scheduledTime: r['scheduled_time'] ? new Date(r['scheduled_time']) : null,
        stageName: r['stage_name'],
        stageType: r['stage_type'],
        status: r['status'],
      })),
    );
  }

  get campaigns(): Observable<Campaign[]> {
    return this.campaignsSubject.asObservable();
  }

  lookupCampaigns(searchTerm: string, status: string[], pageLimit: number): Observable<LookupCampaignsResponse> {
    const statuses = status.map((s) => {
      switch (s) {
        case 'active':
          return Statuses.STATUSES_ACTIVE;
        case 'published':
          return Statuses.STATUSES_PUBLISHED;
        case 'archived':
          return Statuses.STATUSES_ARCHIVED;
        case 'draft':
          return Statuses.STATUSES_DRAFT;
        default:
          return Statuses.STATUSES_UNSPECIFIED;
      }
    });

    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.campaignsSDK.list(partnerId, undefined, statuses, searchTerm, {
          pageSize: pageLimit,
        }),
      ),
      map(
        (cs) =>
          ({
            campaigns: (cs || []).map(
              (v) =>
                ({
                  id: v.campaignId,
                  name: v.name,
                  status: this.convertStatus(v.status),
                }) as SimpleCampaign,
            ),
          }) as LookupCampaignsResponse,
      ),
    );
  }

  getRecipientCampaigns(
    partnerId: string,
    accountGroupId: string,
    email: string,
  ): Observable<RecipientCampaignsResponse> {
    const url = `api/v3/recipient/campaigns/`;
    const body = { partnerId: partnerId, accountGroupId: accountGroupId, email: email };
    return this.apiService.post(url, body, { withCredentials: true });
  }

  hasSnapshot(campaignId: string): Observable<boolean> {
    return this.getCampaign(campaignId).pipe(
      map((result) => {
        return !!result.campaignSchedule.find((schedule) => schedule.stepType === 'snapshot-creation');
      }),
    );
  }

  private convertStatus(status: Statuses): string {
    switch (status) {
      case Statuses.STATUSES_ACTIVE:
        return 'active';
      case Statuses.STATUSES_PUBLISHED:
        return 'published';
      case Statuses.STATUSES_DRAFT:
        return 'draft';
      case Statuses.STATUSES_ARCHIVED:
        return 'archived';
      default:
        console.error('Unexpected status', status);
        return undefined;
    }
  }
}
